import { useEffect } from 'react';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import { useDispatch, useSelector } from '@algoan/dashboard/store/store';
import { messages } from '../../modules/analysis/regular-cash-flows-analysis-messages';
import { AsyncStatus, CashFlowNature as ICashFlowNature } from '@algoan/dashboard/model';
import { CashFlowsTableStyle } from './style';
import { CashFlowNature } from './cash-flow-nature';
import { RemainderToLiveRow } from './remainder-to-live-row';
import { CashFlowCalculationRow } from './cash-flow-calculation-row';
import {
  getEveryCashFlowsFromAnalysis,
  getEveryCashFlowsFromCashFlowCollection,
  hasCashFlowCollection,
} from '@algoan/dashboard/calculators';
import {
  CashFlowCollectionExpandedContext,
  useCashFlowCollectionExpandedState,
} from './cash-flow-collection-expanded-context';
import { ExpandCashFlowCollection } from './expand-cash-flow-collection';
import { accountsFetchingStatusSelector, latestAnalysisConfigSelector } from '@algoan/dashboard/store/selectors';
import { groupedCashFlowsSelector } from '../../selectors';
import { setCheckedCashFlows } from '@algoan/dashboard/store/slice';
import { useDashboardParams } from '@algoan/dashboard/utils';
import { OpenRecategorizationModalProvider } from '../cash-flow-modal/use-recategorization-modal';
import { Tooltip } from '@algoan/deep-dive/tooltip';

interface CashFlowsTableProps {
  columnLabels: string | [string, string, string, string, string];
  checkableNature: ICashFlowNature[];
  showRemainderToLiveRow: boolean;
  showCashFlowCalculationRow: boolean;
  secondaryTable?: React.ReactNode;
}

export const CashFlowsTable: React.FC<CashFlowsTableProps> = ({
  columnLabels,
  checkableNature,
  showRemainderToLiveRow,
  showCashFlowCalculationRow,
  secondaryTable,
}) => {
  const accountsStatus = useSelector(accountsFetchingStatusSelector);
  const intl = useIntl();
  const groupedCashFlows = useSelector(groupedCashFlowsSelector);
  const dispatch = useDispatch();
  const { customerId } = useDashboardParams();
  const analysisConfig = useSelector((state) => latestAnalysisConfigSelector(state, customerId));

  useEffect(() => {
    if (groupedCashFlows === undefined) return;

    const checkedCashFlows = getEveryCashFlowsFromCashFlowCollection(
      getEveryCashFlowsFromAnalysis(groupedCashFlows),
    ).filter(
      (cashFlow) =>
        cashFlow.id &&
        (analysisConfig === undefined ||
          analysisConfig[cashFlow.id]?.isIncludedInRemainderToLive === undefined ||
          analysisConfig[cashFlow.id]?.isIncludedInRemainderToLive === true),
    );

    dispatch(setCheckedCashFlows(checkedCashFlows.map((cashFlow) => cashFlow.index)));
  }, [dispatch, groupedCashFlows, analysisConfig]);

  const { isEveryCashFlowCollectionExpanded, isExpanded, toggleEveryCollectionExpansion, toggleExpansion } =
    useCashFlowCollectionExpandedState(groupedCashFlows);

  return (
    <div data-testid="cash-flow-table">
      {accountsStatus === AsyncStatus.RESOLVED && groupedCashFlows && (
        <CashFlowsTableStyle.Table>
          <S.THead>
            <tr>
              <S.ColumnFiller>
                {hasCashFlowCollection(groupedCashFlows) && (
                  <ExpandCashFlowCollection
                    isEveryCashFlowCollectionExpanded={isEveryCashFlowCollectionExpanded}
                    onExpand={toggleEveryCollectionExpansion}
                  />
                )}
              </S.ColumnFiller>
              {!Array.isArray(columnLabels) && <S.ColumnHeader>{columnLabels}</S.ColumnHeader>}
              {Array.isArray(columnLabels) && <S.ColumnFiller />}
              {groupedCashFlows.calendar.map((calendar) => (
                <S.ColumnHeader key={`header-${calendar.month}`}>
                  <FormattedDate value={calendar.date} month="long" />
                  <S.ColumnSubHeader>
                    <FormattedMessage
                      id="widgets.regular_cash_flows_analysis.nb_days"
                      defaultMessage="sur {nbDays} jours"
                      description="sub header of months columns"
                      values={{
                        nbDays: calendar.nbDays,
                      }}
                    />
                  </S.ColumnSubHeader>
                </S.ColumnHeader>
              ))}
              <S.ColumnHeader>
                <FormattedMessage
                  id="widgets.regular_cash_flows_analysis.total"
                  defaultMessage="Total"
                  description="Header of the last column"
                />
              </S.ColumnHeader>
            </tr>
          </S.THead>
          <S.TBody>
            <S.RowSpacer>
              <td colSpan={3 + groupedCashFlows.calendar.length} />
            </S.RowSpacer>
            <OpenRecategorizationModalProvider>
              <CashFlowCollectionExpandedContext.Provider value={{ isExpanded, toggleExpansion }}>
                {groupedCashFlows.resources.cashFlows.length > 0 && (
                  <CashFlowNature
                    title={intl.formatMessage(messages.categoryResources)}
                    calendar={groupedCashFlows.calendar}
                    cashFlowsByNature={groupedCashFlows.resources}
                    checkable={checkableNature.includes('resources')}
                    firstColumnLabel={Array.isArray(columnLabels) ? columnLabels[0] : undefined}
                  />
                )}
                {groupedCashFlows.incomes.cashFlows.length > 0 && (
                  <CashFlowNature
                    title={intl.formatMessage(messages.categoryIncomes)}
                    calendar={groupedCashFlows.calendar}
                    cashFlowsByNature={groupedCashFlows.incomes}
                    checkable={checkableNature.includes('incomes')}
                    displayUndefinedAsZero
                    firstColumnLabel={Array.isArray(columnLabels) ? columnLabels[1] : undefined}
                  />
                )}
                {groupedCashFlows.fixedCharges.cashFlows.length > 0 && (
                  <CashFlowNature
                    title={intl.formatMessage(messages.categoryFixedCharges)}
                    calendar={groupedCashFlows.calendar}
                    cashFlowsByNature={groupedCashFlows.fixedCharges}
                    checkable={checkableNature.includes('fixedCharges')}
                    firstColumnLabel={Array.isArray(columnLabels) ? columnLabels[2] : undefined}
                  />
                )}
                {groupedCashFlows.expenses.cashFlows.length > 0 && (
                  <CashFlowNature
                    title={intl.formatMessage(messages.categoryExpenses)}
                    calendar={groupedCashFlows.calendar}
                    cashFlowsByNature={groupedCashFlows.expenses}
                    checkable={checkableNature.includes('expenses')}
                    displayUndefinedAsZero
                    firstColumnLabel={Array.isArray(columnLabels) ? columnLabels[3] : undefined}
                  />
                )}
                {groupedCashFlows.loans.cashFlows.length > 0 && (
                  <CashFlowNature
                    title={intl.formatMessage(messages.categoryLoans)}
                    calendar={groupedCashFlows.calendar}
                    cashFlowsByNature={groupedCashFlows.loans}
                    checkable={checkableNature.includes('loans')}
                    firstColumnLabel={Array.isArray(columnLabels) ? columnLabels[4] : undefined}
                  />
                )}
              </CashFlowCollectionExpandedContext.Provider>
            </OpenRecategorizationModalProvider>
            {showRemainderToLiveRow && <RemainderToLiveRow groupedCashFlows={groupedCashFlows} />}
            {showCashFlowCalculationRow && <CashFlowCalculationRow groupedCashFlows={groupedCashFlows} />}

            {secondaryTable}
          </S.TBody>
        </CashFlowsTableStyle.Table>
      )}
      <Tooltip id="cash_flow.rejected_payments.tooltip" />
    </div>
  );
};

const S = {
  LoadingMessage: styled.div`
    color: ${(props) => props.theme.algoanBlue};
    padding: 9px;
  `,
  Error: styled.div`
    color: ${(props) => props.theme.legacy.danger};
  `,
  THead: styled.thead`
    background: #f3f7f9;

    tr {
      height: 25px;
    }
  `,
  ColumnFiller: styled.th`
    flex: 1;
    text-align: left;
  `,
  ColumnHeader: styled.th`
    background-color: ${(props) => props.theme.legacy.lightGrey};
    border-right: white 2px solid;
    font-size: 9px;
    font-weight: bold;
    line-height: 110.4%;
    text-transform: uppercase;
    width: 84px;

    &:nth-child(2) {
      border-radius: 4px 0 0 4px;
    }

    &:last-child {
      border-radius: 0 4px 4px 0;
      border-right: none;
    }
  `,
  ColumnSubHeader: styled.div`
    font-size: 8px;
  `,
  TBody: styled.tbody`
    text-align: right;
  `,
  RowSpacer: styled.tr`
    background: #f3f7f9;
    height: 6px;
  `,
  RowSection: styled.tr`
    background: transparent;
    height: 50px;
  `,
  LoanTitle: styled.td`
    vertical-align: bottom;
    padding-bottom: 10px;
    padding-left: 10px;
    font-size: 12px;
    font-weight: bold;
    height: 15px;
    line-height: 15px;
    margin-left: 10px;
    text-transform: uppercase;
  `,
};
